import { deleteField, doc, getDoc } from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { AlertCircle, ImageUp, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { SectionLabel } from "components/AppShell";
import { RichTextEditor } from "components/RichTextEditor";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogPinnedFooter,
} from "components/ui/dialog";
import { FileInput } from "components/ui/file-input";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Switch } from "components/ui/switch";
import { db } from "integrations/firebase/firestore";
import { storage } from "integrations/firebase/storage";

import { EventArchiveCard } from "../../events/EventArchiveControl";
import { EventDeletionDangerZone } from "../../events/EventDeletionControl";
import { TimezonePicker } from "../../events/TimezonePicker";
import {
  eventTimezones,
  isListedTimezone,
  timezoneReferenceDate,
  type EventTimezone,
} from "../../events/timezones";
import { AdminCommandFeedback } from "../AdminCommandFeedback";
import {
  ATTENDEES_MODULE_HELP,
  ATTENDEES_MODULE_LABEL,
} from "../module-labels";
import type { EventSettings } from "../schemas";
import { useAdminCommand } from "../use-admin-command";
import { isObjectMissing } from "../storage-errors";
import { useEventSettingsWrite } from "../use-event-settings-writes";
import {
  AfterEventNotificationTimeCheck,
  AfterEventSummaryCard,
  useAfterEventSwitch,
} from "./AfterEventSettings";
import { HeroVideoCard } from "./HeroVideoCard";
import { InvitesSection } from "./InvitesSection";
import { LandingPageCard } from "./LandingPageCard";

const DATE = /^\d{4}-\d{2}-\d{2}$/;

const MODULES = [
  { field: "configAgenda", label: "Agenda" },
  { field: "configSpeakers", label: "Speakers" },
  { field: "configAttendees", label: ATTENDEES_MODULE_LABEL },
  { field: "configPartners", label: "Partners" },
  { field: "configVenues", label: "Venues" },
  { field: "configResources", label: "Resources" },
  { field: "configInformation", label: "Info" },
  { field: "configNow", label: "Now" },
  { field: "configAfterEvent", label: "After the event" },
  { field: "configConnections", label: "Connections" },
] as const;

/** The help line under a module switch, where it needs one. */
const MODULE_HELP: Partial<Record<(typeof MODULES)[number]["field"], string>> =
  {
    configAttendees: ATTENDEES_MODULE_HELP,
    configAfterEvent:
      "Your summary, the resources marked for it and the signals summary PDF, at the top of the Overview once the event has ended.",
  };

export function SettingsSection({
  eventId,
  uid,
  event,
  reload,
  onEventDeleted,
}: {
  eventId: string;
  uid: string;
  event: EventSettings;
  reload: () => void;
  /** Where to go once the event is gone. Reloading it only finds nothing. */
  onEventDeleted?: () => void;
}) {
  return (
    <div className="space-y-6" data-testid="settings-content">
      <DetailsCard eventId={eventId} uid={uid} event={event} reload={reload} />
      <HeroImageCard eventId={eventId} event={event} reload={reload} />
      <LandingPageCard eventId={eventId} event={event} reload={reload} />
      <HeroVideoCard eventId={eventId} event={event} reload={reload} />
      <SlugCard eventId={eventId} uid={uid} event={event} reload={reload} />
      <DescriptionCard
        eventId={eventId}
        uid={uid}
        event={event}
        reload={reload}
      />
      <TogglesCard eventId={eventId} uid={uid} event={event} reload={reload} />
      <AfterEventSummaryCard eventId={eventId} uid={uid} />
      <TableRequestsCard eventId={eventId} event={event} reload={reload} />
      <RegistrationSecretCard eventId={eventId} event={event} reload={reload} />
      <InvitesSection
        eventId={eventId}
        uid={uid}
        eventSlug={event.published && event.slug ? event.slug : undefined}
      />
      <EventArchiveCard
        // Keyed by event: this page stays mounted when the route moves to
        // another event's admin page, and the first render after the move
        // must not show the previous event's request.
        key={eventId}
        eventId={eventId}
        eventTitle={event.title}
        uid={uid}
        archived={event.archived === true}
        onChanged={reload}
      />
      <EventDeletionDangerZone
        eventId={eventId}
        eventTitle={event.title}
        uid={uid}
        onSucceeded={onEventDeleted ?? reload}
        leave={
          onEventDeleted
            ? { label: "Back to your events", onLeave: onEventDeleted }
            : undefined
        }
      />
    </div>
  );
}

function DetailsCard({
  eventId,
  uid,
  event,
  reload,
}: {
  eventId: string;
  uid: string;
  event: EventSettings;
  reload: () => void;
}) {
  const { save, saving, error } = useEventSettingsWrite(eventId);
  const [form, setForm] = useState({
    title: event.title,
    location: event.location ?? "",
    startDate: event.startDate,
    endDate: event.endDate,
    timezone: event.timezone ?? "",
  });
  const [invalid, setInvalid] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [movedEnd, setMovedEnd] = useState<{
    endDate: string;
    timeZone: string;
  } | null>(null);

  // The stored zone is offered even when it is not one of ours, so an event
  // seeded or saved with something else keeps it until somebody chooses a
  // replacement. Dropping it would move every session's day key on the next
  // unrelated save.
  // Offsets are the ones in force on the start date being edited, so a
  // September event in Berlin reads GMT+02:00.
  const stored = event.timezone ?? "";
  const timezoneOptions: EventTimezone[] = useMemo(() => {
    const listed = eventTimezones(timezoneReferenceDate(form.startDate));
    return stored && !isListedTimezone(stored)
      ? [{ zone: stored, label: stored }, ...listed]
      : listed;
  }, [form.startDate, stored]);

  const dirty =
    form.title !== event.title ||
    form.location !== (event.location ?? "") ||
    form.startDate !== event.startDate ||
    form.endDate !== event.endDate ||
    form.timezone !== (event.timezone ?? "");

  // The rules re-validate the whole document, so catch what they would reject
  // here and say which field is wrong — a permission-denied cannot tell you.
  function firstProblem(): string | null {
    if (!form.title.trim()) return "Title is required.";
    if (form.title.length > 200)
      return "Title must be 200 characters or fewer.";
    if (!form.timezone.trim()) return "Timezone is required.";
    if (!DATE.test(form.startDate)) return "Start date must be YYYY-MM-DD.";
    if (!DATE.test(form.endDate)) return "End date must be YYYY-MM-DD.";
    if (form.endDate < form.startDate)
      return "End date cannot be before the start date.";
    if (form.location.length > 1000)
      return "Location must be 1000 characters or fewer.";
    return null;
  }

  async function handleSave() {
    const problem = firstProblem();
    setInvalid(problem);
    if (problem) return;
    setSaved(false);
    const ok = await save({
      title: form.title.trim(),
      location: form.location,
      startDate: form.startDate,
      endDate: form.endDate,
      timezone: form.timezone.trim(),
    });
    if (ok) {
      setSaved(true);
      if (
        form.endDate !== event.endDate ||
        form.timezone.trim() !== (event.timezone ?? "")
      ) {
        setMovedEnd({ endDate: form.endDate, timeZone: form.timezone.trim() });
      }
      reload();
    }
  }

  return (
    <Card className="border-border shadow-none">
      <CardContent className="space-y-4 pt-6">
        <SectionLabel>Event details</SectionLabel>

        <Field label="Title" htmlFor="event-title">
          <Input
            id="event-title"
            testId="event-title"
            value={form.title}
            disabled={saving}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
          />
        </Field>
        <Field label="Location" htmlFor="event-location">
          <Input
            id="event-location"
            testId="event-location"
            value={form.location}
            disabled={saving}
            onChange={(e) => setForm({ ...form, location: e.target.value })}
          />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Start date" htmlFor="event-start">
            <Input
              id="event-start"
              testId="event-start"
              value={form.startDate}
              placeholder="2026-09-10"
              disabled={saving}
              onChange={(e) => setForm({ ...form, startDate: e.target.value })}
            />
          </Field>
          <Field label="End date" htmlFor="event-end">
            <Input
              id="event-end"
              testId="event-end"
              value={form.endDate}
              placeholder="2026-09-11"
              disabled={saving}
              onChange={(e) => setForm({ ...form, endDate: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Timezone" htmlFor="event-timezone">
          <TimezonePicker
            id="event-timezone"
            labelId="event-timezone-label"
            testId="event-timezone"
            className="w-full"
            value={form.timezone}
            disabled={saving}
            options={timezoneOptions}
            onChange={(next) => setForm({ ...form, timezone: next })}
          />
        </Field>

        {(invalid || error) && (
          <p
            className="flex items-center gap-2 text-sm text-destructive-text"
            data-testid="event-details-error"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            {invalid ?? error}
          </p>
        )}
        {saved && !dirty && (
          <p
            className="text-sm text-foreground"
            data-testid="event-details-saved"
          >
            Saved
          </p>
        )}
        {/* TNG-96: a booked notification keeps its old send time. Checked
            after a save that moved the end. */}
        {movedEnd && (
          <AfterEventNotificationTimeCheck
            key={`${movedEnd.endDate}|${movedEnd.timeZone}`}
            eventId={eventId}
            uid={uid}
            endDate={movedEnd.endDate}
            timeZone={movedEnd.timeZone}
          />
        )}

        <Button
          variant="secondary"
          onClick={handleSave}
          disabled={saving || !dirty}
          data-testid="save-event-details"
        >
          {saving ? "Saving…" : "Save details"}
        </Button>
      </CardContent>
    </Card>
  );
}

function SlugCard({
  eventId,
  uid,
  event,
  reload,
}: {
  eventId: string;
  uid: string;
  event: EventSettings;
  reload: () => void;
}) {
  const { state, submit, reset } = useAdminCommand();
  const [slug, setSlug] = useState(event.slug ?? "");
  const busy = state.status === "submitting" || state.status === "watching";

  useEffect(() => {
    if (state.status === "succeeded") reload();
  }, [state.status, reload]);

  return (
    <Card className="border-border shadow-none">
      <CardContent className="space-y-3 pt-6">
        <SectionLabel>Public link</SectionLabel>
        <p className="text-sm text-muted-foreground">
          The slug is the event&apos;s public address. Changing it moves the
          public page and retires the old link immediately.
        </p>
        <div className="flex gap-2">
          <Input
            testId="event-slug"
            value={slug}
            placeholder="connect-2026"
            disabled={busy}
            onChange={(e) => setSlug(e.target.value)}
          />
          <Button
            variant="secondary"
            onClick={() =>
              submit("SET_EVENT_SLUG", eventId, uid, { slug: slug.trim() })
            }
            disabled={busy || !slug.trim() || slug.trim() === event.slug}
            data-testid="save-event-slug"
          >
            Save link
          </Button>
        </div>
        <AdminCommandFeedback
          state={state}
          onReset={reset}
          testId="event-slug"
        />
      </CardContent>
    </Card>
  );
}

function DescriptionCard({
  eventId,
  uid,
  event,
  reload,
}: {
  eventId: string;
  uid: string;
  event: EventSettings;
  reload: () => void;
}) {
  const { state, submit, reset } = useAdminCommand();
  const [html, setHtml] = useState(event.description ?? "");
  const busy = state.status === "submitting" || state.status === "watching";

  useEffect(() => {
    if (state.status === "succeeded") reload();
  }, [state.status, reload]);

  return (
    <Card className="border-border shadow-none">
      <CardContent className="space-y-3 pt-6">
        <SectionLabel>Description</SectionLabel>
        <RichTextEditor
          value={html}
          onChange={setHtml}
          disabled={busy}
          testId="event-description-editor"
        />
        <p className="text-xs text-muted-foreground">
          Formatting is sanitized on the server before it reaches the public
          page, so anything unsupported is removed on save.
        </p>
        <Button
          variant="secondary"
          onClick={() =>
            submit("UPDATE_EVENT_DESCRIPTION", eventId, uid, {
              description: html,
            })
          }
          disabled={busy || html === (event.description ?? "")}
          data-testid="save-event-description"
        >
          Save description
        </Button>
        <AdminCommandFeedback
          state={state}
          onReset={reset}
          testId="event-description"
        />
      </CardContent>
    </Card>
  );
}

function TogglesCard({
  eventId,
  uid,
  event,
  reload,
}: {
  eventId: string;
  uid: string;
  event: EventSettings;
  reload: () => void;
}) {
  const { save, saving, error } = useEventSettingsWrite(eventId);
  const afterEvent = useAfterEventSwitch({ eventId, uid, event, reload });

  async function toggle(field: string, next: boolean) {
    // TNG-96: asks first, to offer the "summary is ready" notification.
    if (field === "configAfterEvent") {
      afterEvent.request(next);
      return;
    }
    const ok = await save({ [field]: next });
    if (ok) reload();
  }
  const agendaOn = event.configAgenda ?? MODULE_DEFAULTS.configAgenda;

  return (
    <>
      <Card className="border-border shadow-none">
        <CardContent className="space-y-4 pt-6">
          <SectionLabel>Modules</SectionLabel>
          <div className="grid gap-3 sm:grid-cols-2">
            {MODULES.map((module) => {
              // An ABSENT flag means the default, not false. The public
              // projection defaults agenda, speakers, and attendees to true,
              // so reading Boolean(undefined) here would show those toggles
              // off while attendees were seeing the tabs.
              const checked =
                (event[module.field] as boolean | undefined) ??
                MODULE_DEFAULTS[module.field];
              // The Now tab is built from the agenda and the attendee page
              // hides it while the agenda is off, so switching it on then
              // would do nothing visible. Switching it off stays possible.
              const needsAgenda =
                module.field === "configNow" && !agendaOn && !checked;
              return (
                <ToggleRow
                  key={module.field}
                  label={module.label}
                  description={
                    module.field === "configNow" && !agendaOn
                      ? "Needs the Agenda tab"
                      : MODULE_HELP[module.field]
                  }
                  testId={`toggle-${module.field}`}
                  checked={checked}
                  disabled={
                    saving ||
                    needsAgenda ||
                    (module.field === "configAfterEvent" && afterEvent.busy)
                  }
                  onChange={(next) => toggle(module.field, next)}
                />
              );
            })}
          </div>
          {afterEvent.dialog}
        </CardContent>
      </Card>

      <Card className="border-border shadow-none">
        <CardContent className="space-y-4 pt-6">
          <SectionLabel>Status</SectionLabel>
          <ToggleRow
            label="Published"
            description="Visible on its public link and open for registration."
            testId="toggle-published"
            checked={Boolean(event.published)}
            disabled={saving}
            onChange={(next) => toggle("published", next)}
          />
          {event.attendeeCount != null && (
            <p className="text-sm text-muted-foreground">
              {event.confirmedCount ?? 0} confirmed of {event.attendeeCount}{" "}
              total
            </p>
          )}
          {error && (
            <p
              className="text-sm text-destructive-text"
              data-testid="event-toggle-error"
            >
              {error}
            </p>
          )}
        </CardContent>
      </Card>
    </>
  );
}

/**
 * TNG-92: whether attendees may ask for a table switch, or for a table when
 * they have none. Off by default. While it is on, the Requests tab shows.
 *
 * Switching it off cancels every open request and tells each person, done by
 * a backend trigger on this field, so the switch asks first.
 */
function TableRequestsCard({
  eventId,
  event,
  reload,
}: {
  eventId: string;
  event: EventSettings;
  reload: () => void;
}) {
  const { save, saving, error } = useEventSettingsWrite(eventId);
  const [confirmOff, setConfirmOff] = useState(false);
  const enabled = event.trackSwitchEnabled === true;

  async function setEnabled(next: boolean) {
    const ok = await save({ trackSwitchEnabled: next });
    if (ok) reload();
  }

  return (
    <Card
      className="border-border shadow-none"
      data-testid="table-requests-card"
    >
      <CardContent className="space-y-4 pt-6">
        <SectionLabel>Theme table requests</SectionLabel>
        <ToggleRow
          label="Let attendees request a theme table"
          description="Attendees can ask to switch theme tables, or for one if they have none, until the event ends. You answer them in the Requests tab."
          testId="toggle-trackSwitchEnabled"
          checked={enabled}
          disabled={saving}
          onChange={(next) => {
            if (next) void setEnabled(true);
            else setConfirmOff(true);
          }}
        />
        {error && (
          <p className="text-sm text-destructive-text" role="alert">
            {error}
          </p>
        )}
      </CardContent>
      <Dialog open={confirmOff} onOpenChange={setConfirmOff}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Stop taking theme table requests?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Every open request will be cancelled, and each person gets a
            notification saying so. Answered requests stay as they are.
          </p>
          <DialogPinnedFooter>
            <Button variant="outline" onClick={() => setConfirmOff(false)}>
              Keep them open
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setConfirmOff(false);
                void setEnabled(false);
              }}
              data-testid="confirm-table-requests-off"
            >
              Switch off
            </Button>
          </DialogPinnedFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/**
 * The event's door code for self-registration.
 *
 * The switch and the code are saved together, because the rules refuse an
 * enabled gate without a usable code: saving the switch alone would be a
 * permission-denied that names neither field. Disabling keeps whatever code
 * is stored, so switching the gate back on never strands the event without
 * its old code.
 */
function RegistrationSecretCard({
  eventId,
  event,
  reload,
}: {
  eventId: string;
  event: EventSettings;
  reload: () => void;
}) {
  const { save, saving, error } = useEventSettingsWrite(eventId);
  const [enabled, setEnabled] = useState(
    Boolean(event.registrationSecretEnabled),
  );
  const [secret, setSecret] = useState(event.registrationSecret ?? "");
  const [invalid, setInvalid] = useState<string | null>(null);

  const dirty =
    enabled !== Boolean(event.registrationSecretEnabled) ||
    secret.trim() !== (event.registrationSecret ?? "");

  // The rules couple the switch to a 6–128 character code; saying so here
  // beats a permission error that cannot name the field.
  function firstProblem(): string | null {
    if (secret.length > 128)
      return "The secret must be 128 characters or fewer.";
    if (enabled && secret.trim().length < 6)
      return "The secret must be at least 6 characters.";
    return null;
  }

  async function handleSave() {
    const problem = firstProblem();
    setInvalid(problem);
    if (problem) return;
    if (
      await save({
        registrationSecretEnabled: enabled,
        registrationSecret: secret.trim(),
      })
    ) {
      reload();
    }
  }

  return (
    <Card className="border-border shadow-none">
      <CardContent className="space-y-4 pt-6">
        <SectionLabel>Registration secret</SectionLabel>
        <p className="text-sm text-muted-foreground">
          When the switch is on, people can only register with this code. Share
          it privately — anyone holding it can register.
        </p>
        <ToggleRow
          label="Require the secret"
          description="The public event page asks for the code before registering."
          testId="toggle-registrationSecretEnabled"
          checked={enabled}
          disabled={saving}
          onChange={setEnabled}
        />
        <Field label="Secret" htmlFor="registration-secret-field">
          <Input
            id="registration-secret-field"
            testId="registration-secret-field"
            value={secret}
            placeholder="For example: connect-2026"
            disabled={saving}
            onChange={(e) => setSecret(e.target.value)}
          />
        </Field>
        {(invalid || error) && (
          <p
            className="text-sm text-destructive-text"
            data-testid="registration-secret-error"
          >
            {invalid ?? error}
          </p>
        )}
        <Button
          variant="secondary"
          onClick={handleSave}
          disabled={saving || !dirty}
          data-testid="save-registration-secret"
        >
          {saving ? "Saving…" : "Save secret"}
        </Button>
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label id={`${htmlFor}-label`} htmlFor={htmlFor}>
        {label}
      </Label>
      {children}
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
  testId,
}: {
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  testId: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
        aria-label={label}
        data-testid={testId}
      />
    </div>
  );
}

// storage.rules caps a hero upload at 15MB and accepts png, jpeg or webp only.
// Checked here too, because the rules reject with a permission error that names
// neither the size nor the type.
// The default isValidEvent applies to each absent module flag. Mirrors
// MODULE_DEFAULTS in event_creation.py and _MODULE_DEFAULTS in
// public_event_projection.py; docs/parity-plan.md D5 lists every copy.
const MODULE_DEFAULTS: Record<string, boolean> = {
  configAgenda: true,
  configSpeakers: true,
  configAttendees: true,
  configPartners: false,
  configVenues: false,
  configResources: false,
  configInformation: false,
  configNow: true,
  configAfterEvent: false,
  configConnections: false,
};

const HERO_MAX_BYTES = 15 * 1024 * 1024;
const HERO_TYPES = ["image/png", "image/jpeg", "image/webp"];

/** One object per event, so an upload replaces rather than accumulates. */
const heroPath = (eventId: string): string => `events/${eventId}/hero/hero`;

/**
 * The event's hero image: the file, the point it is cropped around, and the
 * way back to no image at all.
 *
 * Rules and schema have supported `image` plus `imageFocalX`/`imageFocalY`
 * since the backend workstream; nothing ever uploaded one, so every event
 * rendered with the plain gradient header.
 *
 * The focal point matters because the event page crops the image to a wide
 * band. Centre is the right default and wrong for most photographs of people.
 * Event cards crop it too but always around the centre: the membership card
 * the dashboard reads carries `eventImage` and no focal point. It is set by
 * dragging a point over the whole image, because "which part do I keep" is a
 * question about a place in a picture. Two range inputs stay beside it as the
 * keyboard and screen reader path.
 *
 * `imageThumb` and `imageMedium` are left alone. They are in the editable set
 * for a resizing pipeline that does not exist yet; writing the full-size URL
 * into them would look like derivatives existed.
 */
function HeroImageCard({
  eventId,
  event,
  reload,
}: {
  eventId: string;
  event: EventSettings;
  reload: () => void;
}) {
  const { save, saving, error } = useEventSettingsWrite(eventId);
  const [problem, setProblem] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  // Announced through the always-present status line, so a screen reader user
  // hears that a save or a removal landed.
  const [status, setStatus] = useState<string | null>(null);
  // Set when the document was cleared and the object delete failed: the file
  // is still readable at its tokened URL, so the card offers to try again.
  const [orphaned, setOrphaned] = useState(false);
  const [focalX, setFocalX] = useState(event.imageFocalX ?? 50);
  const [focalY, setFocalY] = useState(event.imageFocalY ?? 50);
  const fileInput = useRef<HTMLInputElement>(null);
  const xRange = useRef<HTMLInputElement>(null);
  const retryButton = useRef<HTMLButtonElement>(null);
  // Where focus goes when the removal dialog closes. Radix returns it to the
  // Remove button by default, which unmounts once the image is gone.
  const focusAfterDialog = useRef<HTMLElement | null>(null);

  const focalDirty =
    focalX !== (event.imageFocalX ?? 50) ||
    focalY !== (event.imageFocalY ?? 50);
  const busy = uploading || saving || removing;

  // Focus is moved after the render that re-enables the controls, because a
  // disabled input refuses focus and every control here is disabled while busy.
  const focusWhenIdle = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (busy || !focusWhenIdle.current) return;
    focusWhenIdle.current.focus();
    focusWhenIdle.current = null;
  });

  async function upload(file: File) {
    setProblem(null);
    setStatus(null);
    if (!HERO_TYPES.includes(file.type)) {
      setProblem("Choose a PNG, JPEG or WebP image.");
      return;
    }
    if (file.size > HERO_MAX_BYTES) {
      setProblem("That image is larger than 15 MB.");
      return;
    }
    setUploading(true);
    try {
      // One fixed name per event, so replacing the image does not leave the
      // old object behind — storage.rules allows update on the same path.
      const target = ref(storage, heroPath(eventId));
      await uploadBytes(target, file);
      const url = await getDownloadURL(target);
      // The focal point goes with the image it describes. A new picture
      // starts at the centre, for the same reason removal clears it.
      if (
        await save({
          image: url,
          imageFocalX: deleteField(),
          imageFocalY: deleteField(),
        })
      ) {
        setFocalX(50);
        setFocalY(50);
        // The upload overwrote the file a failed removal left behind.
        setOrphaned(false);
        reload();
      } else
        // The upload rotated the download token on the fixed path, so the URL
        // already on the event is dead whether or not this write landed.
        setProblem(
          "The image uploaded but the event could not be updated. Try saving again.",
        );
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "The upload failed.");
    } finally {
      // The input was disabled for the upload, which dropped focus to the page.
      focusWhenIdle.current = fileInput.current;
      setUploading(false);
    }
  }

  /**
   * Document first, object second, and the order is load-bearing.
   *
   * Deleting the object first and failing the write leaves the event pointing
   * at a URL that 404s, which is a broken image on a page anybody can open.
   * This way round the worst case is a file at a fixed path that the event no
   * longer references. It stays readable by anyone holding its tokened URL
   * (storage.rules lets anyone read a published event's hero), so the card
   * says so and offers a retry; the next upload overwrites it in any case,
   * and only an upload writes `image`, so it cannot reappear on the event. The
   * focal point goes with
   * the image: it describes a picture that is no longer there, and leaving it
   * would silently crop the next upload to the old one's subject.
   */
  async function remove() {
    setProblem(null);
    setStatus(null);
    setRemoving(true);
    try {
      const cleared = await save({
        image: deleteField(),
        imageFocalX: deleteField(),
        imageFocalY: deleteField(),
      });
      if (!cleared) return;
      setFocalX(50);
      setFocalY(50);
      focusAfterDialog.current = fileInput.current;
      if (await deleteFile()) setStatus("Image removed");
      reload();
    } finally {
      // Closed on failure too, as deleting a resource does: an error left
      // under an open dialog is an error nobody reads.
      setConfirmingRemove(false);
      setRemoving(false);
    }
  }

  /** True when the object is gone, including when it already was. */
  async function deleteFile(): Promise<boolean> {
    try {
      await deleteObject(ref(storage, heroPath(eventId)));
    } catch (cause) {
      if (!isObjectMissing(cause)) {
        setOrphaned(true);
        setProblem(
          "The event no longer shows the image. The file could not be deleted, and anyone who has its link can still open it. Uploading a new image replaces the file.",
        );
        return false;
      }
    }
    setOrphaned(false);
    return true;
  }

  /**
   * Re-reads the event before deleting, because `event` is a one-shot read.
   * If anybody uploaded since the failed removal, the path now holds their
   * image and the event points at it; deleting would break the public page,
   * which is what clearing the document first exists to prevent. Their upload
   * overwrote the old file, so there is nothing left to delete.
   *
   * A window remains between this read and the delete. Closing it needs the
   * Storage rule to read the event document, which it does not do today.
   */
  async function retryDelete() {
    setProblem(null);
    setStatus(null);
    setRemoving(true);
    let deleted = false;
    try {
      const current = await getDoc(doc(db, "events", eventId));
      if (typeof current.data()?.image === "string") {
        setOrphaned(false);
        setStatus(
          "A new image was uploaded since, and it replaced the old file.",
        );
        deleted = true;
        reload();
        return;
      }
      deleted = await deleteFile();
      if (deleted) setStatus("File deleted");
    } catch {
      setProblem("The event could not be checked. Try again.");
    } finally {
      // The button unmounts on success; on failure it stays, and it was
      // disabled while busy, which dropped focus to the page either way.
      focusWhenIdle.current = deleted ? fileInput.current : retryButton.current;
      setRemoving(false);
    }
  }

  return (
    <Card className="border-border shadow-none">
      <CardContent className="space-y-4 py-5">
        <SectionLabel>Hero image</SectionLabel>

        <div className="space-y-1.5">
          <Label htmlFor="hero-file">
            {event.image ? "Replace image" : "Upload image"}
          </Label>
          <FileInput
            ref={fileInput}
            id="hero-file"
            testId="hero-file"
            accept={HERO_TYPES.join(",")}
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              // Reset the input, or re-picking the same file after a failure is
              // a no-op: the change event never fires for an unchanged value.
              e.target.value = "";
              if (file) void upload(file);
            }}
          />
          <p className="text-xs text-muted-foreground">
            Use at least 1920 by 640 pixels. The event page crops the image to a
            wide band, about four times as wide as it is tall on a laptop and
            wider on a large monitor. Event cards crop it to about two and a
            half times. PNG, JPEG or WebP, up to 15 MB.
          </p>
        </div>

        {event.image && (
          <HeroFocalPoint
            src={event.image}
            x={focalX}
            y={focalY}
            disabled={busy}
            xRange={xRange}
            onChange={(nextX, nextY) => {
              setStatus(null);
              setFocalX(nextX);
              setFocalY(nextY);
            }}
          />
        )}

        {(problem || error) && (
          <p
            role="alert"
            className="flex items-center gap-2 text-sm text-destructive-text"
            data-testid="hero-error"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            {problem ?? error}
          </p>
        )}

        {orphaned && !event.image && (
          <Button
            ref={retryButton}
            variant="outline"
            disabled={busy}
            data-testid="retry-delete-hero"
            onClick={() => void retryDelete()}
          >
            <Trash2 className="h-4 w-4" />
            {removing ? "Deleting…" : "Try deleting the file again"}
          </Button>
        )}

        {/* Always rendered, and visually hidden rather than display:none while
            empty, so the live region is in the accessibility tree before a
            message arrives; one that appears with its text is often not read. */}
        <p
          role="status"
          className={status ? "text-sm text-foreground" : "sr-only"}
          data-testid="hero-status"
        >
          {status}
        </p>

        {event.image && (
          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              disabled={busy || !focalDirty}
              data-testid="save-hero-focal"
              onClick={async () => {
                setStatus(null);
                if (await save({ imageFocalX: focalX, imageFocalY: focalY })) {
                  setStatus("Focus saved");
                  // The button disables itself once nothing is unsaved, which
                  // drops focus to the page. The first slider is next to it.
                  focusWhenIdle.current = xRange.current;
                  reload();
                }
              }}
            >
              <ImageUp className="h-4 w-4" />
              {saving ? "Saving…" : "Save focus"}
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              data-testid="remove-hero"
              onClick={() => setConfirmingRemove(true)}
            >
              <Trash2 className="h-4 w-4" />
              {removing ? "Removing…" : "Remove image"}
            </Button>
          </div>
        )}

        {uploading && (
          <p
            className="text-sm text-muted-foreground"
            data-testid="hero-uploading"
          >
            Uploading…
          </p>
        )}

        {/* The same shape as deleting a resource, which also destroys an
            uploaded file: a dialog that names what goes and confirms with the
            destructive variant. */}
        <Dialog
          open={confirmingRemove}
          onOpenChange={(next) => !next && setConfirmingRemove(false)}
        >
          <DialogContent
            onCloseAutoFocus={(e) => {
              const target = focusAfterDialog.current;
              if (!target) return;
              e.preventDefault();
              focusAfterDialog.current = null;
              target.focus();
            }}
          >
            <DialogHeader>
              <DialogTitle>Remove the hero image?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              The event page and the event cards show no image until you upload
              another. The uploaded file is deleted, so its link stops working.
            </p>
            <DialogPinnedFooter>
              <Button
                variant="outline"
                onClick={() => setConfirmingRemove(false)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                disabled={busy}
                data-testid="confirm-remove-hero"
                onClick={() => void remove()}
              >
                {removing ? "Removing…" : "Remove image"}
              </Button>
            </DialogPinnedFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

const clampPercent = (value: number): number =>
  Math.min(100, Math.max(0, Math.round(value)));

/**
 * The focal point: a marker dragged over the whole image, and two range inputs
 * bound to the same two numbers.
 *
 * The image is shown uncropped, because the point is a place in the picture
 * rather than a place in the crop: `object-position` reads its two percentages
 * against the image's own box, so the wrapper is sized by the image and the
 * marker's `left`/`top` are the stored values unchanged.
 *
 * The marker is for pointers only and is hidden from assistive technology. The
 * ranges are the keyboard and screen reader path. A custom 2D control cannot
 * match them: NVDA and JAWS stay in browse mode on a button, so arrow keys move
 * the reading cursor, and VoiceOver on iOS and TalkBack only offer their adjust
 * gesture on a native slider or `role="slider"`, the latter unevenly.
 *
 * Pointer events with `touch-action: none` let a finger drag instead of
 * scrolling the page. `preventDefault` on pointerdown suppresses the
 * compatibility mousedown, which would otherwise move focus to the body and
 * start a native image drag.
 *
 * The band underneath is the same image under the event page's laptop crop,
 * live, so the effect of a drag is visible rather than inferred.
 */
function HeroFocalPoint({
  src,
  x,
  y,
  disabled,
  onChange,
  xRange,
}: {
  src: string;
  x: number;
  y: number;
  disabled?: boolean;
  onChange: (x: number, y: number) => void;
  /** Lets the card return focus here after a save disables its button. */
  xRange: React.RefObject<HTMLInputElement | null>;
}) {
  const area = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  function pointFrom(clientX: number, clientY: number) {
    const box = area.current?.getBoundingClientRect();
    if (!box || box.width === 0 || box.height === 0) return;
    const nextX = ((clientX - box.left) / box.width) * 100;
    const nextY = ((clientY - box.top) / box.height) * 100;
    // A coordinate the browser did not supply would store NaN on the event,
    // and the rules accept it: NaN is a number and every comparison against
    // the 0-100 bounds is false, so `data.get(...) >= 0 && <= 100` is the only
    // thing standing between a stray event and an uncroppable image.
    if (!Number.isFinite(nextX) || !Number.isFinite(nextY)) return;
    onChange(clampPercent(nextX), clampPercent(nextY));
  }

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (disabled) return;
    event.preventDefault();
    dragging.current = true;
    // Without capture a fast drag that leaves the image stops updating.
    area.current?.setPointerCapture?.(event.pointerId);
    pointFrom(event.clientX, event.clientY);
  }

  function onPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    if (!dragging.current || disabled) return;
    pointFrom(event.clientX, event.clientY);
  }

  function endDrag(event: React.PointerEvent<HTMLDivElement>) {
    dragging.current = false;
    area.current?.releasePointerCapture?.(event.pointerId);
  }

  return (
    <div className="space-y-3">
      <div
        ref={area}
        className="relative inline-block max-w-full cursor-crosshair touch-none select-none"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        data-testid="hero-focal-area"
      >
        <img
          src={src}
          alt=""
          draggable={false}
          data-testid="hero-preview"
          className="block max-h-64 w-auto max-w-full rounded-lg"
        />
        {/* White inside, navy outside, so the marker reads on light and dark
            parts of any photograph. */}
        <span
          aria-hidden="true"
          data-testid="hero-focal-point"
          className="pointer-events-none absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-white bg-hb-blue-fill shadow-card ring-2 ring-hb-dusk-deep"
          style={{ left: `${x}%`, top: `${y}%` }}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        The point marks the part of the image that stays in view when the event
        page crops it. Drag it onto the subject, or click or tap where it should
        go. The two sliders below move it too.
      </p>

      <div className="grid gap-4 sm:grid-cols-2">
        <FocalRange
          id="hero-focal-x"
          label="Across"
          valueText={`${x}% across`}
          value={x}
          disabled={disabled}
          inputRef={xRange}
          onChange={(next) => onChange(next, y)}
        />
        <FocalRange
          id="hero-focal-y"
          label="Down"
          valueText={`${y}% down`}
          value={y}
          disabled={disabled}
          onChange={(next) => onChange(x, next)}
        />
      </div>

      <div className="space-y-1.5">
        <p className="text-xs font-medium text-foreground">
          What the event page keeps on a laptop
        </p>
        <div className="aspect-[4/1] w-full overflow-hidden rounded-lg bg-muted">
          <img
            src={src}
            alt=""
            data-testid="hero-crop-preview"
            className="h-full w-full object-cover"
            style={{ objectPosition: `${x}% ${y}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function FocalRange({
  id,
  label,
  valueText,
  value,
  disabled,
  inputRef,
  onChange,
}: {
  id: string;
  label: string;
  valueText: string;
  value: number;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  onChange: (next: number) => void;
}) {
  // The label stays fixed and the number beside it is hidden from assistive
  // technology: the value is announced once, through aria-valuetext, and a
  // name that changed on every step would be announced as well.
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline justify-between text-xs">
        <Label htmlFor={id} className="text-xs">
          {label}
        </Label>
        <span aria-hidden="true" className="text-muted-foreground">
          {value}%
        </span>
      </div>
      <input
        ref={inputRef}
        id={id}
        data-testid={id}
        type="range"
        min={0}
        max={100}
        value={value}
        disabled={disabled}
        aria-valuetext={valueText}
        onChange={(e) => onChange(clampPercent(Number(e.target.value)))}
        className="w-full rounded-sm accent-lit ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      />
    </div>
  );
}
